import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// DIO2 Thr92Ala (homozygous) + DIO1 reduced + BDNF Val66Met —
// the "normal TSH" that masked six years of cellular hypothyroidism.
// The aha: TSH measures pituitary T3, not tissue T3. When DIO2 is impaired,
// the pituitary can be satisfied while every other cell in the body is starved.

export const thyroidCiara: Scenario = {
  slug: 'thyroid-ciara',
  series: 'Hormones & Mood',
  title: "Her TSH was normal. Her cells were still asking for thyroid hormone.",
  socialHook:
    "Ciara, 49, took levothyroxine for six years and heard the same thing every year: your TSH is normal. She still felt cold, foggy, exhausted, and stuck in a body that would not respond. Her genetics explained the missing piece: normal TSH does not always mean thyroid effect is adequate in the tissues.",

  meta: {
    description:
      "A fictional case study: Ciara has hypothyroidism and a normal TSH on levothyroxine, yet remains symptomatic with fatigue, weight gain, brain fog, cold intolerance, and low mood. DIO2 Thr92Ala (homozygous), reduced DIO1 activity, and BDNF Val66Met explain why T4-only treatment can leave tissue thyroid effect inadequate in a specific subset of patients.",
  },

  persona: {
    name: 'Ciara',
    age: 49,
    sex: 'Female',
    ancestry: 'Irish',
    occupation: 'Social worker',
    chiefConcern:
      "Ciara was diagnosed with hypothyroidism six years ago and started on levothyroxine (T4). At every annual review her family doctor shows her the TSH result — currently 1.8 mIU/L, well within the reference range — and confirms her thyroid is well-controlled. She has every symptom she had before diagnosis: persistent fatigue, 18 lb of weight gain she cannot shift despite eating carefully, brain fog that makes her question whether she can continue in her demanding job, cold intolerance, low mood, constipation, and dry skin. She has been referred to psychiatry for depression. She does not believe she is depressed — she believes she is physically ill and cannot get anyone to listen.",
    familyHistory:
      "Mother: hypothyroid on levothyroxine, similarly symptomatic despite being told TSH was normal. No known cardiac or neurological history.",
  },

  clinicalPicture: {
    symptoms: [
      'Persistent fatigue — exhausted on waking, worse through the afternoon',
      "Weight gain of 8kg since starting levothyroxine, despite careful eating and no change in activity",
      'Brain fog — difficulty concentrating, word-finding problems, slowed thinking',
      'Cold intolerance — wears layers in summer, hands and feet consistently cold',
    ],
    labs: [
      {
        name: 'Reverse T3',
        value: '32 ng/dL',
        refRange: '9.2–24.1 ng/dL',
        flag: 'high',
      },
      {
        name: 'T4:T3 Conversion Ratio',
        value: '4.5',
        refRange: 'Ideally <4.0',
        flag: 'high',
      },
    ],
    medications: ['Levothyroxine (T4) — dose stable for 4 years'],
    supplements: ['None currently'],
    lifestyle: [
      'Diet: careful eater — low processed food, adequate vegetables, not calorie-restricting',
      'Exercise: light walking — fatigue limits more',
      'Alcohol: 2–3 drinks/week',
      'Sleep: 8–9 hours, unrefreshing',
      'Non-smoker',
    ],
  },

  genetics: [
    {
      gene: 'DIO2',
      variant: 'Thr92Ala (homozygous)',
      rsid: 'rs225014',
      genotype: 'Ala/Ala — two copies of the reduced-function variant',
      plainMeaning:
      "Her medication gets T4 into the body, but some tissues still may not get enough active thyroid effect. Ciara carries two copies of a variant that makes it harder for brain, muscle, and fat cells to convert T4 into active T3.",
      technicalDetail:
        "DIO2 (iodothyronine deiodinase type 2) is the enzyme that converts T4 — the storage form of thyroid hormone delivered by levothyroxine — into T3, the active form that enters cells and drives metabolism. The Thr92Ala variant reduces DIO2 activity specifically in the brain, muscle, and adipose tissue. The pituitary gland uses a different enzyme (DIO1) for its own conversion, so TSH reflects pituitary T3 — not tissue T3. Her TSH signals that the pituitary is satisfied; it says less about whether muscles, brain, or fat cells are also adequately supplied. An estimated 12–16% of the population carry two copies of this variant.",
    },
    {
      gene: 'DIO1',
      variant: 'rs2235544 (reduced-function)',
      rsid: 'rs2235544',
      genotype: 'Reduced deiodinase type 1 activity',
      plainMeaning:
      "The circulating active hormone signal may also run lower than expected. That adds a second layer to the tissue-conversion problem.",
      technicalDetail:
        "DIO1 handles T4-to-T3 conversion in the liver and bloodstream — the fraction captured by a Free T3 blood test. A reduced-function variant means circulating T3 is lower than expected for a given T4 level, and her Free T3 sits at the very bottom of the normal reference range as a direct result. Together DIO1 and DIO2 create a two-layer deficit: lower circulating T3 in the blood, and poor conversion to T3 in the cells that most depend on it.",
    },
    {
      gene: 'BDNF',
      variant: 'Val66Met',
      rsid: 'rs6265',
      genotype: 'Reduced activity-dependent BDNF secretion',
      plainMeaning:
      "Her brain may feel the thyroid shortfall more sharply. This variant affects a mood-and-memory support protein that thyroid hormone helps maintain.",
      technicalDetail:
        "BDNF (brain-derived neurotrophic factor) is required for neuroplasticity, memory consolidation, and mood regulation. Thyroid hormone — specifically T3 — directly upregulates BDNF expression. The Val66Met variant already reduces activity-dependent BDNF secretion at baseline. Chronically low tissue T3 (from DIO2 impairment) suppresses BDNF below that already-reduced level. This is the biological mechanism behind her cognitive symptoms and the low mood that has not responded to antidepressants: SSRIs act through serotonergic and monoaminergic pathways; the deficit here is in neuroplasticity driven by T3 deficiency.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "Six years of normal results. Six years of the same symptoms.",
      body: "Ciara was diagnosed with hypothyroidism at 43 after years of fatigue and weight gain. Starting levothyroxine felt like the answer. Within a few months her TSH was normal. Her family doctor was pleased. The symptoms did not change. The morning commute still felt like a marathon. The weight did not move despite careful eating. The brain fog made her question whether she could continue as a social worker. At every annual review she raised the same concerns. At every annual review she was shown the TSH number and told the thyroid was controlled. By year four she had been referred to psychiatry and tried an antidepressant for six months. Nothing changed. She did not feel depressed. She felt physically ill and unheard.",
    },
    {
      type: 'narrative',
      heading: "Normal TSH does not always mean enough thyroid effect",
      body: "TSH is produced by the pituitary gland as a signal to the thyroid. When the pituitary has enough T3, the active form of thyroid hormone, TSH settles into the normal range. The key word is pituitary. TSH shows whether the pituitary is satisfied. It does not directly prove that muscle, brain, fat, and heart tissue are getting the same active thyroid effect. For most people, that distinction may not matter much. For Ciara, it was the missing clue.",
    },
    {
      type: 'reveal',
      heading: "Her cells may not be converting enough T4 into T3",
      body: "Ciara's medication supplies T4, but her brain, muscles, and fat cells may struggle to turn it into usable T3. She carries two copies of the DIO2 Thr92Ala variant, which points to reduced conversion in those tissues. Her active T3 sits at the very bottom of the normal range, and her reverse T3 is elevated — an inactive form that can compete at the receptor without turning the signal on. Her TSH can look perfect because the pituitary is satisfied while other tissues may still be under-supplied.",
    },
    {
      type: 'reveal',
      heading: "The mood symptoms were downstream of the thyroid clue",
      body: "Ciara's low mood and brain fog were not separate mysteries. A reduced DIO1 pattern can lower the active T3 signal measured in blood, while DIO2 can limit conversion inside key tissues. Her BDNF variant adds another reason her brain may notice that shortfall: T3 helps support pathways involved in memory, focus, and mood. Antidepressants target different systems. If tissue T3 is still inadequate, treating the mood symptoms alone can miss the cause.",
    },
    {
      type: 'action',
      heading: "Five conversations and interventions — in order of priority",
      items: [
        "Request a discussion of combination T4/T3 therapy (levothyroxine plus liothyronine) with her family doctor or endocrinologist. Combination therapy delivers T3 directly, bypassing part of the conversion step that appears impaired. The conversation should be framed around the DIO2 genotype and persistent symptoms with normal TSH.",
        "Address selenium and ferritin urgently because they support thyroid hormone conversion. Selenium is a mineral cofactor for deiodinase enzymes. Selenomethionine 200µg/day is the evidence-supported form. Ferritin is functionally low and needs to reach above 70 µg/L; iron is required both by the thyroid to synthesize hormone and by enzymes that convert it. Ferrous bisglycinate is a well-tolerated oral iron form; recheck in 8 weeks.",
        "Optimize vitamin D because she is meaningfully deficient. Vitamin D receptors are expressed in thyroid tissue, and deficiency can compound both thyroid function and mood, which is especially relevant given the BDNF variant. Vitamin D3 3,000–4,000 IU daily until recheck, co-supplemented with K2 (100µg MK-7), is a discussion point for her clinician.",
        "Ask for reverse T3 to be measured alongside Free T3 at her next review. Elevated reverse T3 is rarely tested in routine thyroid panels but is clinically meaningful — a high ratio confirms the conversion problem and provides objective evidence for the combination therapy conversation with the endocrinologist. Having this documented in her notes strengthens the clinical case considerably.",
        "Pause before adding another antidepressant until tissue T3 adequacy has been reviewed. Her low mood may be linked to T3 deficiency acting through BDNF-related pathways. The genetics provide a biological explanation for why cognitive and mood symptoms are prominent, and they give her clinician a clearer target to investigate first.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'checkin' as const,
      heading: "Ciara's 14-day nutritional support check-in",
      goalLabel: "Daily energy, mental clarity & cold intolerance — while awaiting T4/T3 review",
      caption:
        "Ciara started selenium and iron supplementation on day 1 while waiting for her endocrinology appointment to discuss T4/T3 combination therapy. Nutritional cofactors work slowly; the first week showed little change. By day 9 the first noticeable shift arrived. The lab recheck and medication review are the next step.",
      days: [
        {
          day: 1,
          scores: [
            { label: 'Energy', value: 2 },
            { label: 'Mental clarity', value: 3 },
            { label: 'Cold intolerance', value: 2 },
          ],
          note: "Started selenomethionine and ferrous bisglycinate this morning. No expectation of quick results — selenium takes weeks to accumulate. Endocrinologist referral requested from family doctor.",
        },
        {
          day: 2,
          scores: [
            { label: 'Energy', value: 2 },
            { label: 'Mental clarity', value: 3 },
            { label: 'Cold intolerance', value: 2 },
          ],
          note: "No change. Expected. Iron supplement easy on the stomach with the bisglycinate form.",
        },
        {
          day: 3,
          scores: [
            { label: 'Energy', value: 3 },
            { label: 'Mental clarity', value: 3 },
            { label: 'Cold intolerance', value: 2 },
          ],
          note: "Marginally more alert this morning but hard to separate from a good sleep. Not reading too much into it.",
        },
        {
          day: 4,
          scores: [
            { label: 'Energy', value: 3 },
            { label: 'Mental clarity', value: 3 },
            { label: 'Cold intolerance', value: 2 },
          ],
          note: "Flat. The usual afternoon heaviness. Grateful to have a reason to keep going — the genetics gave me a framework when nothing else did.",
        },
        {
          day: 5,
          scores: [
            { label: 'Energy', value: 3 },
            { label: 'Mental clarity', value: 4 },
            { label: 'Cold intolerance', value: 2 },
          ],
          note: "Slightly clearer head this morning. Finished a case report at work without losing my thread. Small thing but I noticed it.",
        },
        {
          day: 6,
          scores: [
            { label: 'Energy', value: 4 },
            { label: 'Mental clarity', value: 4 },
            { label: 'Cold intolerance', value: 3 },
          ],
          note: "Energy fractionally better this morning. Still tired but the heavy dragging feeling was less for a few hours.",
          milestone: "First slight energy improvement",
        },
        {
          day: 7,
          scores: [
            { label: 'Energy', value: 4 },
            { label: 'Mental clarity', value: 4 },
            { label: 'Cold intolerance', value: 3 },
          ],
          note: "One week on supplements. The improvements are small but they are real — and they are the first changes in six years of treatment.",
        },
        {
          day: 8,
          scores: [
            { label: 'Energy', value: 4 },
            { label: 'Mental clarity', value: 5 },
            { label: 'Cold intolerance', value: 3 },
          ],
          note: "Clearer today. Held a difficult client conversation and felt present for it — not struggling to track the thread.",
        },
        {
          day: 9,
          scores: [
            { label: 'Energy', value: 5 },
            { label: 'Mental clarity', value: 5 },
            { label: 'Cold intolerance', value: 5 },
          ],
          note: "First morning I felt warm when I woke up. I cannot remember the last time I was not cold first thing. It lasted about two hours and then I needed a jumper again. But it happened.",
          milestone: "First morning I felt warm when I woke up",
        },
        {
          day: 10,
          scores: [
            { label: 'Energy', value: 5 },
            { label: 'Mental clarity', value: 5 },
            { label: 'Cold intolerance', value: 4 },
          ],
          note: "Warm again on waking. Energy still not where I need it — but the cold intolerance shifting is significant. That has been there every single morning for years.",
        },
        {
          day: 11,
          scores: [
            { label: 'Energy', value: 5 },
            { label: 'Mental clarity', value: 6 },
            { label: 'Cold intolerance', value: 4 },
          ],
          note: "Managed a full work day without the 2pm crash. First time in months. Cautiously optimistic.",
        },
        {
          day: 12,
          scores: [
            { label: 'Energy', value: 6 },
            { label: 'Mental clarity', value: 6 },
            { label: 'Cold intolerance', value: 5 },
          ],
          note: "Two people at work commented that I seemed more 'present' today. I did not tell them about any of this. They just noticed.",
        },
        {
          day: 13,
          scores: [
            { label: 'Energy', value: 6 },
            { label: 'Mental clarity', value: 6 },
            { label: 'Cold intolerance', value: 5 },
          ],
          note: "Stable. Still a long way from where I want to be — but this is more improvement than the previous six years of treatment combined.",
        },
        {
          day: 14,
          scores: [
            { label: 'Energy', value: 6 },
            { label: 'Mental clarity', value: 7 },
            { label: 'Cold intolerance', value: 5 },
          ],
          note: "Endocrinologist appointment confirmed. I am going in with the DIO2 genotype, the rT3 result, and two weeks of check-in data. For the first time in six years I feel like I have something concrete to advocate with.",
          milestone: "Endocrinologist appointment confirmed — T4/T3 trial requested",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'thyroid-ciara',
    label: "Explore your thyroid genetics in Coach",
  },
}
